import { BrowserModule, Title } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { RouterModule } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginLayoutComponent } from './shared/layouts/login-layout/login-layout.component';
import { MainLayoutComponent } from './shared/layouts/main-layout/main-layout.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthService } from './core/services/auth.service';

import { environment } from '../environments/environment';
import { HeaderConfigInterceptor } from './core/interceptor/header-config-interceptor.service';
import { ConstantsProviders } from './core/providers/constants/constants';
import { ConstantsKeyProviders } from './core/providers/constants/constants-key';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { NgxSpinnerModule } from 'ngx-spinner';

import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgScrollbarModule } from 'ngx-scrollbar';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { ApiEliteInterceptor } from './core/interceptor/api-elite.interceptor';
import {
  API_COBOT_INFERENCE_URL,
  API_COBOT_TRAIN_URL,
} from './core/providers/constants/api.constants';
import {
  TRANSLATE_HTTP_LOADER_CONFIG,
  TranslateHttpLoader,
} from '@ngx-translate/http-loader';
import { SidenavComponent } from './core/components/sidenav/sidenav.component';
import {
  MatDialogModule,
  MAT_DIALOG_DEFAULT_OPTIONS,
} from '@angular/material/dialog';
import { MatNativeDateModule } from '@angular/material/core';
import { NgProgressbar } from 'ngx-progressbar';
import { NgProgressHttp, progressInterceptor } from 'ngx-progressbar/http';

// MSAL V2
const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string): void {
  // console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azure.client_id,
      authority: environment.azure.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiBase, [
    `${environment.azure.client_id}/.default`,
  ]);
  protectedResourceMap.set(environment.otherApis.netsuite, [
    `${environment.azure.client_id}/.default`,
  ]);
  protectedResourceMap.set(environment.otherApis.netsuiteV2, [
    `${environment.azure.client_id}/.default`,
  ]);
  protectedResourceMap.set(environment.otherApis.files, [
    `${environment.azure.client_id}/.default`,
  ]);
  protectedResourceMap.set(environment.otherApis.apiBaseElite, [
    `${environment.azure.client_id}/.default`,
  ]);
  protectedResourceMap.set(environment.otherApis.asana, [
    `${environment.azure.client_id}/.default`,
  ]);
  protectedResourceMap.set(environment.otherApis.cobot, [
    `${environment.azure.client_id}/.default`,
  ]);
  protectedResourceMap.set(API_COBOT_INFERENCE_URL, [
    `${environment.azure.client_id}/.default`,
  ]);
  protectedResourceMap.set(API_COBOT_TRAIN_URL, [
    `${environment.azure.client_id}/.default`,
  ]);

  // protectedResourceMap.set(environment.otherApis.files, [
  //   `${environment.azure.client_id}/.default`,
  // ]);
  protectedResourceMap.set(environment.graph.profile, ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      // scopes: ['user.read'],
      scopes: environment.azure.consentScopes,
    },
  };
}

@NgModule({
  declarations: [AppComponent, LoginLayoutComponent, MainLayoutComponent],
  imports: [
    MatDialogModule,
    // ReportModule,
    MatNativeDateModule,
    BrowserModule,
    RouterModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MsalModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    NgxSpinnerModule,
    DragDropModule,
    NgScrollbarModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader,
      },
    }),
    SidenavComponent,
    NgProgressbar,
    NgProgressHttp,
  ],
  providers: [
    provideHttpClient(
      withInterceptors([progressInterceptor]),
      withInterceptorsFromDi()
    ),
    AuthService,
    MatDatepickerModule,
    Title,
    ConstantsProviders,
    ConstantsKeyProviders,
    // Open dialogs with no enter/exit animation app-wide. The Material dialog
    // enter animation occasionally fails to settle (container stays at
    // opacity:0 / scale .7) when open() runs outside Angular's zone — e.g.
    // after BrowserPrint/WebSocket callbacks — leaving only the backdrop and
    // no visible card, with no console error. Zeroing the durations makes the
    // dialog jump straight to its final visible state so it never depends on
    // the animation completing. Per-call configs still override these.
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { enterAnimationDuration: 0, exitAnimationDuration: 0 },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderConfigInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiEliteInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    TranslateService,
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: { prefix: './assets/i18n/', suffix: '.json' },
    },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  exports: [],
})
export class AppModule {
  constructor(private translateService: TranslateService) {
    // Default ES
    this.translateService.setDefaultLang('es');
  }
}
